import React from 'react';

import { useSelector } from 'react-redux';
import { Switch, Route, useParams, useHistory, useRouteMatch } from 'react-router-dom';

import StickyHeader from 'components/Layouts/Admin/Dashboard/StickyHeader';
import { Collapse, Panel } from 'components/shared/Collapse';
import GlobalSearchInput from 'components/shared/GlobalSearchInput';
import PlusCircle from 'components/shared/Icon/icons/PlusCircle';
import Modal from 'components/shared/Modal';
import PageHeader from 'components/shared/PageHeader';
import { LIBRARY_RESOURCE, CONTINENTS } from 'constants/index';
import { PAGE_DETAILS_PATH, PAGE_RESOURCES_PATH } from 'constants/routes';
import { useAccountPath } from 'hooks/useAccountPath';
import { BlockerForAgent } from 'hooks/useSubscriptionBlockContent';
import useTheme from 'hooks/useTheme';
import { isAgentSelector, isSuperAdminSelector } from 'store/user/selectors';
import { toQuery } from 'utils/query';

import { useGlobalSearch } from '../shared/functions';
import Preview from '../shared/Preview';
import { ResourceLibraryContext, useResourceLibraryContext, useResourceLibraryContextValue } from './context';
import Table from './Table';

const continentKeys = Object.keys(CONTINENTS);

function Continent() {
  const { content } = useParams();
  const history = useHistory();
  const getPath = useAccountPath();
  const {
    data,
    isLoading,
    pagination,
    setLimit,
    setPage,
    setSort,
    queryParams,
    previewModalData,
    handleOpenPreview,
    handleClosePreview,
    searchResultCounters,
    handleCollapse,
  } = useResourceLibraryContext();

  return (
    <>
      <Collapse
        defaultActive={continentKeys.indexOf(content)}
        titleTextProps={{ 'data-e2e-element-type': 'tableTitle' }}
        data-e2e-widget-type="pageContent"
        data-e2e-widget-id="dropdownTableViewWidget"
      >
        {continentKeys.map((key) => {
          const title = `${CONTINENTS[key]} Properties`;

          return (
            <Panel
              key={key}
              title={title}
              onClick={() => handleCollapse(key)}
              searchResultsCount={searchResultCounters[key]}
            >
              <div style={{ position: 'relative' }}>
                <Table
                  data={data}
                  params={queryParams}
                  pagination={pagination}
                  onEdit={(id) =>
                    history.push(getPath(PAGE_RESOURCES_PATH, { pageId: id, libType: LIBRARY_RESOURCE }), {
                      from: history.location.pathname + history.location.search,
                      libType: LIBRARY_RESOURCE,
                    })
                  }
                  onOrder={setSort}
                  onView={handleOpenPreview}
                  isLoading={isLoading}
                  setLimit={setLimit}
                  setPage={setPage}
                />
              </div>
            </Panel>
          );
        })}
      </Collapse>

      <Modal
        noPadding
        size="lg"
        isOpen={previewModalData.isOpen}
        toClose={handleClosePreview}
        children={<Preview {...previewModalData} />}
      />
    </>
  );
}

function ResourceLibraryInner() {
  const history = useHistory();
  const getPath = useAccountPath();
  const query = toQuery({ type: 'property' });
  const { isLoading, isAllowed } = useResourceLibraryContext();
  const { searchValue, handleChangeSearchValue, handleClearSearchValue, handleSubmit } = useGlobalSearch();
  const isAgent = useSelector(isAgentSelector);
  const isSuper = useSelector(isSuperAdminSelector);

  useTheme('accent');

  return (
    <>
      {!isAllowed && <BlockerForAgent />}

      <StickyHeader>
        <PageHeader
          title="Resource Library"
          button={
            (!isAgent && !isSuper && (
              <PageHeader.Button
                onClick={() =>
                  history.push(`${getPath(PAGE_DETAILS_PATH, { pageId: 0, libType: LIBRARY_RESOURCE })}?${query}`)
                }
                icon={PlusCircle}
              >
                Create New
              </PageHeader.Button>
            )) ||
            null
          }
          field={
            <GlobalSearchInput
              value={searchValue}
              placeholder="Search resource library..."
              onChange={handleChangeSearchValue}
              onClear={handleClearSearchValue}
              onSubmit={handleSubmit}
              isLoading={isLoading}
            />
          }
        />

        <Continent />
      </StickyHeader>
    </>
  );
}

function ResourceLibraryWithContext() {
  const { content } = useParams();
  const contextValue = useResourceLibraryContextValue({ content });

  return (
    <ResourceLibraryContext.Provider value={contextValue}>
      <ResourceLibraryInner />
    </ResourceLibraryContext.Provider>
  );
}

function ResourceLibraryWithRoutes() {
  const { path } = useRouteMatch();

  return (
    <Switch>
      <Route exact path={path}>
        <ResourceLibraryWithContext />
      </Route>
      <Route path={`${path}/:content`}>
        <ResourceLibraryWithContext />
      </Route>
    </Switch>
  );
}

export default ResourceLibraryWithRoutes;
